import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ProjectResourceSummary } from '@/api/client';
import {
  consumeDirtySummaryProjectIds,
  getDirtySummaryProjectIds,
  SUMMARIES_BATCH_RELOAD_QUERY_KEY,
  SUMMARIES_DELTA_BUMP_QUERY_KEY,
} from '@/lib/summariesBatchReload';

const EMPTY_RESOURCE: ProjectResourceSummary = {
  noteCount: 0,
  recentNoteCount: 0,
  fileCount: 0,
  recentFileCount: 0,
  recordedSessionCount: 0,
  plannedSessionCount: 0,
  plannerStatus: null,
};

/**
 * Сводки комментариев и ресурсов для строк таблицы:
 * - при mount / появлении новых id — один батч только по «дырявым» id (дельта);
 * - при bumpSummariesBatchReload — один батч по всем видимым id;
 * - при invalidateSummariesForProjectIds / bumpSummariesDelta — батч только по очищенным id;
 * - кэш нормализован по `['cellCommentSummaries', id]` / `['projectResourceSummaries', id]`.
 */
export function useProjectTableSummaries(projectIds: string[]) {
  const queryClient = useQueryClient();
  const sortedIds = useMemo(() => [...new Set(projectIds)].sort(), [projectIds]);
  const idsKey = sortedIds.join('|');

  const { data: reloadSignal = 0 } = useQuery({
    queryKey: SUMMARIES_BATCH_RELOAD_QUERY_KEY,
    queryFn: () => 0,
    staleTime: Infinity,
    initialData: 0,
  });

  const { data: deltaSignal } = useQuery({
    queryKey: SUMMARIES_DELTA_BUMP_QUERY_KEY,
    queryFn: () => ({ at: 0, projectIds: [] as string[] }),
    staleTime: Infinity,
    initialData: { at: 0, projectIds: [] as string[] },
  });

  const lastHandledReloadRef = useRef<number>(0);
  const [cacheEpoch, setCacheEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const applyPayloads = (
      cell: Awaited<ReturnType<typeof api.projects.cellCommentSummaries>>,
      res: Awaited<ReturnType<typeof api.projects.projectResourceSummaries>>,
      cellSummaryTargetIds: string[]
    ) => {
      for (const id of cellSummaryTargetIds) {
        queryClient.setQueryData(['cellCommentSummaries', id], cell.summaries[id] ?? {});
      }
      for (const [id, row] of Object.entries(res.summaries)) {
        queryClient.setQueryData(['projectResourceSummaries', id], row ?? EMPTY_RESOURCE);
      }
    };

    const run = async () => {
      if (sortedIds.length === 0) return;

      const sig = typeof reloadSignal === 'number' ? reloadSignal : 0;
      const shouldFullBatch = sig !== 0 && sig !== lastHandledReloadRef.current;

      if (shouldFullBatch) {
        const [cell, res] = await Promise.all([
          api.projects.cellCommentSummaries(sortedIds),
          api.projects.projectResourceSummaries(sortedIds),
        ]);
        if (cancelled) return;
        applyPayloads(cell, res, sortedIds);
        consumeDirtySummaryProjectIds(queryClient, sortedIds);
        lastHandledReloadRef.current = sig;
        setCacheEpoch((e) => e + 1);
        return;
      }

      const dirtySummaryIds = new Set(getDirtySummaryProjectIds(queryClient));
      const idsToFetch = sortedIds.filter(
        (id) =>
          dirtySummaryIds.has(id) ||
          queryClient.getQueryData(['cellCommentSummaries', id]) === undefined ||
          queryClient.getQueryData(['projectResourceSummaries', id]) === undefined
      );
      if (idsToFetch.length === 0) return;

      const [cell, res] = await Promise.all([
        api.projects.cellCommentSummaries(idsToFetch),
        api.projects.projectResourceSummaries(idsToFetch),
      ]);
      if (cancelled) return;
      applyPayloads(cell, res, idsToFetch);
      consumeDirtySummaryProjectIds(queryClient, idsToFetch);
      setCacheEpoch((e) => e + 1);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [idsKey, reloadSignal, deltaSignal?.at, queryClient, sortedIds]);

  const cellQueries = useQueries({
    queries: sortedIds.map((id) => ({
      queryKey: ['cellCommentSummaries', id] as const,
      queryFn: async () => ({}),
      enabled: false,
      staleTime: Infinity,
    })),
  });

  const resourceQueries = useQueries({
    queries: sortedIds.map((id) => ({
      queryKey: ['projectResourceSummaries', id] as const,
      queryFn: async () => EMPTY_RESOURCE,
      enabled: false,
      staleTime: Infinity,
    })),
  });

  const cellEpoch = cellQueries.map((q) => q.dataUpdatedAt).join('|');
  const cellCommentSummaries = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    for (let i = 0; i < sortedIds.length; i++) {
      const id = sortedIds[i]!;
      const row = cellQueries[i]?.data as Record<string, number> | undefined;
      if (row && typeof row === 'object') out[id] = row;
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cellEpoch + cacheEpoch вместо нестабильной ссылки queries
  }, [sortedIds, cellEpoch, cacheEpoch]);

  const resourceEpoch = resourceQueries.map((q) => q.dataUpdatedAt).join('|');
  const projectResourceSummaries = useMemo(() => {
    const out: Record<string, ProjectResourceSummary> = {};
    for (let i = 0; i < sortedIds.length; i++) {
      const id = sortedIds[i]!;
      const row = resourceQueries[i]?.data;
      if (row) out[id] = row;
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedIds, resourceEpoch, cacheEpoch]);

  return { cellCommentSummaries, projectResourceSummaries };
}
